import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchAttendanceReport } from "../redux/actions/attendanceReportActions";
import Papa from "papaparse";
import {
  FiSearch,
  FiDownload,
  FiFilter,
  FiUser,
  FiCalendar,
  FiClock,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiAlertCircle,
} from "react-icons/fi";

const AttendanceReport = () => {
  const dispatch = useDispatch();
  const { reportData, meta, loading } = useSelector(
    (state) => state.attendanceReport,
  );

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [activeSearch, setActiveSearch] = useState("");

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const params = {
        page: currentPage,
        limit: 10,
        fromDate,
        toDate,
        employeeName: searchTerm,
      };
      dispatch(fetchAttendanceReport(params));
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [
    dispatch,
    currentPage,
    fromDate,
    toDate,
    searchTerm,
  ]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatTime12h = (timeStr) => {
    if (!timeStr || timeStr === "-") return "";
    const cleanTime = timeStr.replace(/\./g, ":");
    const [hours, minutes] = cleanTime.split(":");
    let h = parseInt(hours);
    const m = minutes.padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${String(h).padStart(2, "0")}:${m} ${ampm}`;
  };

  const formatDecimalHours = (decimal) => {
  if (!decimal || isNaN(decimal) || Number(decimal) <= 0) return "0 hrs 0 mins";
  const totalHours = parseFloat(decimal);
  const hours = Math.floor(totalHours);
  const minutes = Math.round((totalHours - hours) * 60);
  return `${hours} hrs ${minutes} mins`;
};

  const handleSearch = (e) => {
    if (e.key === "Enter") setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setActiveSearch("");
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
  };

  const handleDownloadCSV = () => {
    const headers = [
      "Date",
      "Employee Name",
      "Check-In Time",
      "Check-Out Time",
      "Total Working Hours",
      "Expected Working Hours",
      "Total Working Days",
      "Total Present",
      "Total Absent",
     
    ];
    const csvRows = [headers];
    reportData.forEach((emp) => {
      emp.attendance.forEach((att, index) => {
        const isFirstRow = index === 0;
        csvRows.push([
          formatDate(att.report_date),
          emp.employee_name,
          formatTime12h(att.check_in) || "",
          formatTime12h(att.check_out) || "",
          att.working_hours || "",
          att.expected_working_hours || "",
          isFirstRow ? emp.total_working_days : "",
          isFirstRow ? emp.total_present : "",
          isFirstRow ? emp.total_absent : "",
        ]);
      });
    });
    const csv = Papa.unparse(csvRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Attendance_Report_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Hero Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {" "}
            Attendance Report
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Detailed analytics of present, absent, and leave records
          </p>
        </div>
        <button
          onClick={handleDownloadCSV}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl transition-all shadow-sm"
        >
          <FiDownload /> Export Report
        </button>
      </div>

      {/* Filter Bar */}
      <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search employee name and press Enter..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearch}
              className="w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-blue-100 hover:text-blue-700 px-5 py-3 rounded-xl text-gray-600 dark:text-gray-300"
            >
              <FiFilter /> Filters
            </button>
            {(searchTerm || fromDate || toDate) && (
              <button
                onClick={clearFilters}
                className="text-red-500 font-medium px-2"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5 pt-5 border-t border-gray-100 dark:border-gray-700">
           
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-400 uppercase">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border-none rounded-lg p-3 text-sm dark:text-white"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-400 uppercase">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border-none rounded-lg p-3 text-sm dark:text-white"
              />
            </div>
          </div>
        )}
      </div>

      {/* Grouped Content */}
      <div className="space-y-6">
        {loading && !searchTerm && (
          <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 z-50 flex justify-center pt-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {reportData.length === 0 && !loading ? (
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-20 text-center border dark:border-gray-700">
            <FiAlertCircle size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">
              No attendance report found for this criteria.
            </p>
          </div>
        ) : (
          reportData.map((emp) => (
            <div
              key={emp.employee_name}
              className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col lg:flex-row overflow-hidden"
            >
              {/* Sidebar Stats */}
              <div className="w-full lg:w-72 p-6 bg-gray-100 dark:bg-gray-700/20 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-500">
                    <FiUser size={24} />
                  </div>
                  <div className="overflow-hidden">
                    <h3 className="font-bold text-gray-900 dark:text-white truncate">
                      {emp.employee_name}
                    </h3>
                    
                  </div>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-xl border dark:border-gray-700 flex justify-between">
                    <span className="text-gray-700 dark:text-gray-400 font-bold uppercase">
                      Work Days
                    </span>
                    <span className="font-bold dark:text-white">
                      {emp.total_working_days}
                    </span>
                  </div>
                  <div className="p-3 bg-green-500/5 rounded-xl border border-green-500/20 flex justify-between">
                    <span className="text-green-600 font-bold uppercase">
                      Present
                    </span>
                    <span className="font-bold text-green-600">
                      {emp.total_present}
                    </span>
                  </div>
                  <div className="p-3 bg-red-500/5 rounded-xl border border-red-500/20 flex justify-between">
                    <span className="text-red-500 font-bold uppercase">
                      Absent
                    </span>
                    <span className="font-bold text-red-500">
                      {emp.total_absent}
                    </span>
                  </div>
                  
                </div>
              </div>

              {/* Attendance Table */}
              <div className="flex-1 overflow-hidden">
                <div className="max-h-[360px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
                  <table className="w-full text-xs border-collapse">
                    <thead className="sticky top-0 bg-gray-200 dark:bg-gray-700 z-10 border-b dark:border-gray-600">
                      <tr className="text-gray-600 dark:text-gray-300">
                        <th className="p-4 text-left border-r dark:border-gray-600">
                          Date
                        </th>
                        <th className="p-4 text-left border-r dark:border-gray-600">
                          Status
                        </th>
                        <th className="p-4 text-left border-r dark:border-gray-600">
                          Check In/Out
                        </th>
                        <th className="p-4 text-left border-r dark:border-gray-600">
                          Work Hours
                        </th>
                        <th className="p-4 text-left">Target</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {emp.attendance.map((day) => (
                        <tr
                          key={formatDate(day.report_date)}
                          className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition"
                        >
                          <td className="p-4 border-r dark:border-gray-700 font-medium dark:text-gray-300">
                            {formatDate(day.report_date)}
                          </td>
                          <td className="p-4">
                            <span
                              className={`px-2 py-1 rounded-md font-bold uppercase text-[10px] ${
                                day.attendance_status === "present"
                                  ? "text-green-600 dark:text-green-500"
                                  : day.attendance_status === "absent"
                                    ? "text-red-600 dark:text-red-500"
                                    : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {day.attendance_status}
                            </span>
                          </td>
                          <td className="p-4 border-r dark:border-gray-700">
                            <div className="flex items-center gap-2 dark:text-white">
                              <FiClock className="text-gray-600" />
                              <span>{formatTime12h(day.check_in) || "."}</span>
                              <span className="text-gray-400">→</span>
                              <span>{formatTime12h(day.check_out) || "."}</span>
                            </div>
                          </td>
                          <td
                            className={`p-4 border-r dark:border-gray-700 font-bold ${Number(day.working_hours) > 0 ? "text-blue-500" : "text-gray-400"}`}
                          >
                            {formatDecimalHours(day.working_hours)}
                          </td>
                          <td className="p-4 text-gray-600 dark:text-gray-300 italic">
                            {day.expected_working_hours} Hrs
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-xl bg-white dark:bg-gray-800 border dark:border-gray-700 disabled:opacity-30"
          >
            <FiChevronLeft size={24} className="dark:text-white" />
          </button>
          <span className="text-sm font-bold text-gray-500">
            Page {currentPage} of {meta.totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((p) => Math.min(meta.totalPages, p + 1))
            }
            disabled={currentPage === meta.totalPages}
            className="p-2 rounded-xl bg-white dark:bg-gray-800 border dark:border-gray-700 disabled:opacity-30"
          >
            <FiChevronRight size={24} className="dark:text-white" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AttendanceReport;
